import { useState } from 'react';
import { EyeOff } from 'lucide-react';
import { conditionMet } from '@hcc/types';
import type { FormSchema, FieldCondition } from '@hcc/types';
import { Button } from '../../ui/Button';
import { FieldView } from '../formfields/FieldView';
import type { FormValues, FieldValue } from '../formfields/formValues';
import { hasSpecialCategory } from './draftOps';

interface FormPreviewProps {
  schema: FormSchema;
  title: string;
  description?: string;
}

/** A human-readable "shown only when X is Y" for a conditional field. */
function describeCondition(schema: FormSchema, cond: FieldCondition): string {
  const src = schema.pages.flatMap((p) => p.fields).find((f) => f.key === cond.field);
  const label = src?.label || cond.field;
  const opts =
    src?.type === 'yes_no'
      ? [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]
      : src?.options ?? [];
  const values = cond.equals.map((v) => opts.find((o) => o.value === v)?.label ?? v);
  return values.length
    ? `Shown only when "${label}" is ${values.join(' or ')}`
    : `Shown only when "${label}" matches a condition`;
}

/**
 * Live preview of the form as the inductee will see it: step chips, one page
 * at a time, Back/Next, and the consent gate at the end when the form holds
 * special-category fields. Interactive but never submits.
 */
export function FormPreview({ schema, title, description }: FormPreviewProps) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<FormValues>({});
  const [consented, setConsented] = useState(false);

  const pages = schema.pages.length ? schema.pages : [{ fields: [] }];
  // Mirror the inductee: conditionally hidden pages drop out of the step flow,
  // so toggling the controlling answer here reveals / hides them just as it
  // will on the real form.
  const visibleIdx = pages.map((_, i) => i).filter((i) => conditionMet(pages[i]!.visible_if, values));
  const flow = visibleIdx.length ? visibleIdx : [0];
  const clamped = Math.min(step, pages.length - 1);
  const curIdx = flow.includes(clamped) ? clamped : flow.find((i) => i >= clamped) ?? flow[flow.length - 1]!;
  const pos = flow.indexOf(curIdx);
  const page = pages[curIdx]!;
  const isLast = pos === flow.length - 1;
  const needsConsent = hasSpecialCategory(schema);

  function set(key: string, v: FieldValue) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  return (
    <div className="rounded-token border border-line/10 bg-surface p-4">
      <div className="mb-3">
        <div className="text-base font-semibold text-fg">{title || 'Untitled form'}</div>
        {description ? <p className="mt-0.5 text-sm text-fg-muted">{description}</p> : null}
      </div>

      {flow.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {flow.map((i, n) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              className={
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ' +
                (i === curIdx
                  ? 'bg-accent text-fg-on-accent'
                  : 'bg-surface-2 text-fg-muted hover:bg-surface-inset')
              }
            >
              {pages[i]!.visible_if ? <EyeOff className="h-3 w-3" aria-label="Conditional page" /> : null}
              {n + 1}. {pages[i]!.title || `Page ${n + 1}`}
            </button>
          ))}
        </div>
      ) : null}

      {page.visible_if ? (
        <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-fg-dim">
          <EyeOff className="h-3.5 w-3.5 shrink-0" />
          {describeCondition(schema, page.visible_if)} (hidden by default)
        </div>
      ) : null}
      {page.title && flow.length > 1 ? (
        <div className="mb-2 text-sm font-semibold text-fg">{page.title}</div>
      ) : null}
      {page.description ? <p className="mb-3 text-sm text-fg-muted">{page.description}</p> : null}

      <div className="space-y-4">
        {page.fields.length === 0 ? (
          <p className="rounded-token-sm bg-surface-2 px-3 py-6 text-center text-sm text-fg-dim">
            No fields on this page yet.
          </p>
        ) : (
          page.fields.map((f, i) =>
            f.visible_if ? (
              // Conditional fields are faded with a caption so it's obvious
              // they're hidden by default and what reveals them.
              <div
                key={f.key || i}
                className="rounded-token-sm border border-dashed border-line-strong/20 bg-surface-2/40 p-3 opacity-60"
              >
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-fg-dim">
                  <EyeOff className="h-3.5 w-3.5 shrink-0" />
                  {describeCondition(schema, f.visible_if)}
                </div>
                <FieldView field={f} value={values[f.key] ?? null} onChange={(v) => set(f.key, v)} />
              </div>
            ) : (
              <FieldView key={f.key || i} field={f} value={values[f.key] ?? null} onChange={(v) => set(f.key, v)} />
            ),
          )
        )}
      </div>

      {isLast && needsConsent ? (
        <label className="mt-4 flex items-start gap-2 rounded-token-sm border border-warning-soft/30 bg-warning-soft/15 p-3 text-sm text-warning-soft-fg">
          <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} className="mt-0.5" />
          <span>{schema.consent?.statement ?? 'I consent to the processing of this data.'}</span>
        </label>
      ) : null}

      <div className="mt-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" disabled={pos === 0} onClick={() => setStep(flow[Math.max(pos - 1, 0)]!)}>
          Back
        </Button>
        {isLast ? (
          <Button variant="primary" size="sm" disabled>
            Submit
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => setStep(flow[Math.min(pos + 1, flow.length - 1)]!)}>
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
